import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, AGE_VERIFICATION_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { getUrlParam } from '@/methods/urlParams'
import { cx } from '@/utils/cx'
import {
  authenticationResponseToJSON,
  isConditionalMediationAvailable,
  parseRequestOptions,
  PublicKeyCredentialRequestOptionsJSON,
} from '@/methods/webauthn'
import './AgeVerification.css'

interface CreateEidTransactionResponse {
  proceedUrl: string
}

const MAX_PASSKEY_ATTEMPTS = 3

// Set by the BE proceed redirect: the methods the transaction offers (`passkey`, `eid`, `pictureId`)
// and the age it verifies.
type Method = 'passkey' | 'eid' | 'pictureId'

const getMethods = (): Method[] => {
  const methodsParam = getUrlParam('methods')
  // No param: a redirect from a BE that predates the method screen; it only knows passkey and eID
  if (!methodsParam) return ['passkey', 'eid']

  const methods = (['passkey', 'eid', 'pictureId'] as const).filter((method) =>
    methodsParam.split(',').includes(method)
  )
  // A passkey can only be reused after a first verification, so there must always be another method
  return methods.some((method) => method !== 'passkey') ? methods : [...methods, 'eid']
}

type PasskeyAuthResult = 'verified' | 'failed' | 'expired'

const isPasskeyExpiredError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as { response?: { data?: { code?: string } } }).response?.data?.code === 'PasskeyExpired'

type Busy = Method | null

// The tile's call-to-action arrow, drawn inline (not via `Icon`) so it is a block that centres in its
// circle; the generic icon wrapper is inline-block and leaves the glyph sitting on the text baseline.
const MethodArrow = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.5 8H13.5M9 3.5L13.5 8L9 12.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const AgeVerificationStartPage = observer(() => {
  const store = useContext(StoreContext)
  const {
    ageVerificationPage: { start: trans },
  } = store.TranslationsState.translations

  const methods = getMethods()
  const requiredAge = getUrlParam('requiredAge') ?? ''
  // Set by the BE proceed redirect when a passkey was registered from this browser
  const hasKnownPasskey = getUrlParam('hasPasskey') === 'true'

  const [isLoading, setIsLoading] = useState(true)
  const [busy, setBusy] = useState<Busy>(null)
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [error, setError] = useState('')

  const startEidVerification = async () => {
    setBusy('eid')
    setError('')
    try {
      const { data } = await axiosInstance.post<CreateEidTransactionResponse>(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/eid`,
        {},
        { withCredentials: true }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (e) {
      devLog('Error creating eid transaction:', e)
      setError(trans.genericError)
      setBusy(null)
    }
  }

  // PictureID: the BE starts the identity-document verification and the user is sent to it; it comes
  // back to the PictureID return page, which waits for the result.
  const startPictureIdVerification = async () => {
    setBusy('pictureId')
    setError('')
    try {
      const { data } = await axiosInstance.post<CreateEidTransactionResponse>(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/picture-id`,
        {},
        { withCredentials: true }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (e) {
      devLog('Error creating PictureID transaction:', e)
      setError(trans.genericError)
      setBusy(null)
    }
  }

  const authenticateWithPasskey = async (): Promise<PasskeyAuthResult> => {
    try {
      const { data: requestOptions } =
        await axiosInstance.post<PublicKeyCredentialRequestOptionsJSON>(
          `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/auth/start`,
          {},
          { withCredentials: true }
        )

      const credential = (await navigator.credentials.get({
        publicKey: parseRequestOptions(requestOptions),
      })) as PublicKeyCredential | null

      if (!credential) {
        return 'failed'
      }

      // Responds with 204 on success and throws (4xx/5xx) on verification failure
      await axiosInstance.post(
        `${API_URL}/${AGE_VERIFICATION_SERVICE_PATH}/auth/verify`,
        authenticationResponseToJSON(credential),
        { withCredentials: true }
      )

      return 'verified'
    } catch (e) {
      devLog('Passkey authentication failed:', e)
      return isPasskeyExpiredError(e) ? 'expired' : 'failed'
    }
  }

  // `isAutomatic`: started by the page itself because a passkey is known to exist on this browser.
  // Safari on iOS refuses WebAuthn requests that are not triggered by a tap, and a user may also just
  // dismiss the automatic prompt, so an automatic failure is silent: the method screen appears with the
  // passkey button highlighted and no attempt is counted.
  const signInWithPasskey = async (isAutomatic = false) => {
    setBusy('passkey')
    setError('')

    const result = await authenticateWithPasskey()

    if (result === 'verified') {
      store.ScenarioState.nextPage()
      return
    }

    if (isAutomatic && result === 'failed') {
      setBusy(null)
      return
    }

    if (result === 'expired') {
      setError(trans.passkeyExpired)
      setBusy(null)
      return
    }

    const attempts = failedAttempts + 1
    setFailedAttempts(attempts)
    setError(attempts >= MAX_PASSKEY_ATTEMPTS ? trans.passkeyAttemptsExhausted : trans.passkeyError)
    setBusy(null)
  }

  useEffect(() => {
    isConditionalMediationAvailable()
      .then((isAvailable) => {
        setIsPasskeyAvailable(isAvailable)
        // `hasPasskey` is set by the BE proceed redirect when a passkey was registered from this browser
        // (a cookie on the web-api domain): offer it right away, the user only has to confirm it. A failed
        // or cancelled prompt falls back to the method screen as usual.
        if (isAvailable && methods.includes('passkey') && hasKnownPasskey) {
          void signInWithPasskey(true)
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  const showPasskey =
    methods.includes('passkey') && isPasskeyAvailable && failedAttempts < MAX_PASSKEY_ATTEMPTS
  const isBusy = busy !== null

  const methodTile = (
    method: 'eid' | 'pictureId',
    icon: 'bank-id' | 'picture-id',
    title: string,
    subtitle: string,
    onClick: () => void
  ) => (
    <button
      type="button"
      className={cx('age-verification__method', isBusy && 'age-verification__method--disabled')}
      onClick={onClick}
      disabled={isBusy}
      data-testid={`age-verification-${method}`}
    >
      <span className="age-verification__method-icon">
        <Icon type={icon} size="38px" />
      </span>
      <span className="age-verification__method-content">
        <span className="age-verification__method-title">{title}</span>
        <span className="age-verification__method-subtitle">{subtitle}</span>
      </span>
      <span className="age-verification__method-arrow">
        {busy === method ? <LoadingSpinner inButton /> : <MethodArrow />}
      </span>
    </button>
  )

  return (
    <div className="page-container mobile-m-0">
      <div className="layout__content-container-with-scroll">
        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph-centered">
          {requiredAge
            ? trans.description.replace('{age}', requiredAge)
            : trans.descriptionWithoutAge}
        </p>

        {error && (
          <div className="generic__onboarding-item">
            <div className="generic__onboarding-icon">
              <Icon type="warning" size="32px" color="var(--text-input-error-color, #941616)" />
            </div>
            <span className="generic__onboarding-single-line generic__error-message">{error}</span>
          </div>
        )}

        <div className="age-verification__methods">
          {methods.includes('eid') &&
            methodTile(
              'eid',
              'bank-id',
              trans.eidTitle,
              trans.eidSubtitle,
              () => void startEidVerification()
            )}
          {methods.includes('pictureId') &&
            methodTile(
              'pictureId',
              'picture-id',
              trans.pictureIdTitle,
              trans.pictureIdSubtitle,
              () => void startPictureIdVerification()
            )}
        </div>

        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>

      {showPasskey && (
        <div className="layout__content-container-bottom-fixed-centered age-verification__bottom">
          <div className="layout__vertically-centered-container">
            <Button
              onClick={() => void signInWithPasskey()}
              tabIndex={0}
              className={hasKnownPasskey ? 'button--proceed' : 'button--secondary'}
              disabled={isBusy}
            >
              {busy === 'passkey' && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  busy === 'passkey' && 'generic__button-text--loading'
                )}
                data-testId="sign-in-with-passkey"
              >
                {trans.signInWithPasskey}
              </span>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
})
